/* eslint-disable react-refresh/only-export-components -- provider + its hook
   belong together, matching app/contexts/* and app/hec/context/*. */
import { type ReactNode, createContext, useContext } from "react";

import type { PriceDisplay } from "~/shop/utils/price-display";

export interface TelehealthPdpPrice {
  /** Resolved catalog price for the product/variant on screen. */
  display: PriceDisplay | null;
  /** Recurring-period suffix ("/mo"), or null when the cadence is unproven. */
  period: string | null;
  /**
   * Intake attribution for a CTA rendered in this buy column. Exists so an
   * authored Builder CTA reports the same `src`/`cta` as the coded one: a
   * `TelehealthFirstQuestion` block defaults to `source: "first-question"`,
   * which would silently reattribute every PDP-sourced intake start for any
   * medication a marketer authors an entry for.
   */
  ctaContext?: { source: string; ctaLocation: string };
}

const TelehealthPdpPriceContext = createContext<TelehealthPdpPrice | null>(
  null,
);

/**
 * Publishes the treatment PDP's LIVE catalog price to whatever renders inside
 * the buy column — including Builder blocks, which can't reach the route's
 * `VariantSelector` render-prop scope on their own.
 *
 * Exists so "use the default price" in the Builder pricing section (ET-216)
 * resolves through the exact same path the coded column uses
 * (`resolvePriceFromSelectedVariant` + `telehealthPricePeriod`) rather than a
 * second, drifting derivation. The route computes it once; consumers only render.
 */
export function TelehealthPdpPriceProvider({
  value,
  children,
}: {
  value: TelehealthPdpPrice;
  children: ReactNode;
}) {
  return (
    <TelehealthPdpPriceContext.Provider value={value}>
      {children}
    </TelehealthPdpPriceContext.Provider>
  );
}

/**
 * The live price, or `null` outside a treatment PDP buy column — a Builder block
 * authored onto some other page has no catalog product behind it, so
 * "use the default price" there must render nothing rather than guess.
 */
export function useOptionalTelehealthPdpPrice(): TelehealthPdpPrice | null {
  return useContext(TelehealthPdpPriceContext);
}
